import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Zap, Package, Sparkles, Calendar } from "lucide-react";
import SEO from "../components/SEO";

export default function UVCottonCandyPage() {
  return (
    <div className="min-h-screen pt-20" data-testid="uv-cotton-candy-page">
      <SEO 
        title="UV-Activated Cotton Candy | Non-Toxic, Dye-Free Birthday Cake"
        description="The original non-toxic, naturally UV-activated cotton candy. Dye-free Birthday Cake flavor that glows under blacklight. Wholesale for nightclubs, casinos, resorts, and entertainment venues."
        keywords="UV cotton candy, blacklight cotton candy, glow cotton candy, nightclub cotton candy, dye-free cotton candy, non-toxic UV cotton candy"
        canonical="/uv-cotton-candy"
      />
      
      {/* Hero - Dark Theme */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 bg-purple-500/30 text-purple-200 text-sm font-bold px-4 py-2 rounded-full mb-6">
            <Zap size={16} />
            THE ORIGINAL
          </span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            UV-Activated <span className="text-purple-300">Cotton Candy</span>
          </h1>
          <p className="text-lg md:text-xl text-purple-200 mb-8 leading-relaxed max-w-2xl mx-auto">
            Be the first in your market to offer cotton candy that glows under blacklight. Created by Cotton Candy Girl—the original.
          </p>
          
          <div className="max-w-2xl mx-auto mb-10 bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-purple-400/40 text-left" data-testid="uv-explainer">
            <p className="font-semibold text-lg mb-2">Non-toxic. Dye-free. Naturally UV-activated.</p>
            <p className="text-purple-200 text-sm leading-relaxed">
              Our Birthday Cake cotton candy contains no artificial dyes. The glow is activated by natural ingredients that react under UV light—in normal lighting it looks like classic cotton candy, and under blacklight it lights up. Currently offered in Birthday Cake only.
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/wholesale">
              <Button 
                className="bg-white text-purple-900 hover:bg-purple-100 rounded-full px-8 py-6 text-lg font-semibold shadow-lg transition-all"
                data-testid="uv-wholesale-btn"
              >
                <Package className="mr-2" size={20} />
                Wholesale Inquiry
              </Button>
            </Link>
            <Link to="/events">
              <Button 
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg font-semibold transition-all"
                data-testid="uv-events-btn"
              >
                <Calendar className="mr-2" size={20} />
                Live Event Spinning
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* What Makes It Special */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#4A4A4A] mb-4">What Makes It Special</h2>
            <p className="text-lg text-[#888888]">The wow factor your venue needs</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-8">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mx-auto mb-6">
                <Zap className="text-white" size={40} />
              </div>
              <h3 className="text-xl font-bold text-[#4A4A4A] mb-3">Naturally UV-Activated</h3>
              <p className="text-[#888888]">Non-toxic and dye-free. Natural ingredients react under UV light to create an eye-catching glow customers love to photograph and share.</p>
            </div>
            
            <div className="text-center p-8">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-pink-500 to-orange-500 flex items-center justify-center mx-auto mb-6">
                <span className="text-4xl">🍭</span>
              </div>
              <h3 className="text-xl font-bold text-[#4A4A4A] mb-3">Birthday Cake Flavor</h3>
              <p className="text-[#888888]">Our UV-activated line is offered in Birthday Cake—a crowd-pleasing classic that tastes exactly like the cotton candy your customers expect.</p>
            </div>
            
            <div className="text-center p-8">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center mx-auto mb-6">
                <Sparkles className="text-white" size={40} />
              </div>
              <h3 className="text-xl font-bold text-[#4A4A4A] mb-3">Instagram-Worthy</h3>
              <p className="text-[#888888]">The glow effect creates instant social media content. Customers will tag your venue and spread the word.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Perfect For */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-900 to-purple-900 text-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Perfect For</h2>
            <p className="text-purple-200">Venues and events with blacklight or UV lighting</p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: "🎵", name: "Nightclubs" },
              { icon: "🎤", name: "Concerts" },
              { icon: "🎰", name: "Casinos" },
              { icon: "🎢", name: "Theme Parks" },
              { icon: "🏨", name: "Resorts" },
              { icon: "🍸", name: "Bars & Lounges" },
              { icon: "🎭", name: "Entertainment Venues" },
              { icon: "🏢", name: "Corporate Activations" },
            ].map((item) => (
              <div 
                key={item.name}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 text-center hover:bg-white/20 transition-colors"
              >
                <div className="text-3xl mb-2">{item.icon}</div>
                <p className="font-semibold text-sm">{item.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Available Formats */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#4A4A4A] mb-4">Available Formats</h2>
            <p className="text-lg text-[#888888]">Two ways to bring UV cotton candy to your customers</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Pre-Packaged */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-3xl p-8 border border-purple-100">
              <div className="w-16 h-16 rounded-2xl bg-purple-500 flex items-center justify-center mb-6">
                <Package className="text-white" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-[#4A4A4A] mb-3">Pre-Packaged Tubs</h3>
              <p className="text-[#888888] mb-6">
                32oz tamper-evident containers, perfect for retail sales at your venue. Stock your shelves with glow-in-the-dark cotton candy.
              </p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-center gap-2 text-[#4A4A4A]">
                  <span className="text-purple-500">✓</span> 4-6 week shelf life
                </li>
                <li className="flex items-center gap-2 text-[#4A4A4A]">
                  <span className="text-purple-500">✓</span> Blank or branded labels
                </li>
                <li className="flex items-center gap-2 text-[#4A4A4A]">
                  <span className="text-purple-500">✓</span> Wholesale pricing available
                </li>
              </ul>
              <Link to="/wholesale">
                <Button className="w-full bg-purple-500 hover:bg-purple-600 text-white rounded-full py-4 font-semibold">
                  Request Wholesale Info
                </Button>
              </Link>
            </div>
            
            {/* Live Spinning */}
            <div className="bg-gradient-to-br from-pink-50 to-orange-50 rounded-3xl p-8 border border-pink-100">
              <div className="w-16 h-16 rounded-2xl bg-[#FF9EAA] flex items-center justify-center mb-6">
                <Calendar className="text-white" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-[#4A4A4A] mb-3">Live Event Spinning</h3>
              <p className="text-[#888888] mb-6">
                Fresh UV-activated cotton candy spun on site for select venue and corporate events. Accepted by request based on availability.
              </p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-center gap-2 text-[#4A4A4A]">
                  <span className="text-[#FF9EAA]">✓</span> Fresh spun on site with a professional setup
                </li>
                <li className="flex items-center gap-2 text-[#4A4A4A]">
                  <span className="text-[#FF9EAA]">✓</span> Las Vegas & Boise service areas
                </li>
                <li className="flex items-center gap-2 text-[#4A4A4A]">
                  <span className="text-[#FF9EAA]">✓</span> Limited availability—request to be considered
                </li>
              </ul>
              <Link to="/events">
                <Button className="w-full bg-[#FF9EAA] hover:bg-[#FF8FAB] text-white rounded-full py-4 font-semibold">
                  Submit a Live Event Request
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-purple-600 to-pink-600 text-white">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Glow?</h2>
          <p className="text-lg text-white/90 mb-8">
            Be the first venue in your area to offer non-toxic, naturally UV-activated cotton candy.
          </p>
          <Link to="/wholesale">
            <Button 
              className="bg-white text-purple-600 hover:bg-purple-100 rounded-full px-10 py-6 text-lg font-semibold shadow-lg transition-all"
              data-testid="uv-cta-btn"
            >
              Get Started Today
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
